/**
 * 📣 پیام به دستهٔ کاربران — نسخهٔ ۲ (شرط‌های کاملاً منعطف).
 *
 *   «کی‌ها؟»  — گروه‌های شرط: داخل هر گروه «همه/هر کدام»، بین گروه‌ها «همه/هر کدام»؛
 *               هر شرط یک دکمهٔ «نه» دارد؛ عدد با ≥ ≤ =؛ ویژگی‌های انتخابی (فروشگاه/محصول/…)
 *               با جست‌وجو؛ به‌علاوه/به‌جز با چسباندن شماره‌ها. شمارش زنده + جملهٔ فارسی مخاطب.
 *   «چی بگم؟» — جای‌گذاری {name} {points} …، چهار کانال با reach، زمان‌بندی، تست، ارسال.
 *
 * کاتالوگ/دسته‌های آماده/جای‌گذاری‌ها از GET /admin/announcements/meta — هیچ لیست hardcode‌ای.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Button, Checkbox, CircularProgress, MenuItem, TextField, Tooltip } from '@mui/material';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import BookmarksRoundedIcon from '@mui/icons-material/BookmarksRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ProtectedRoute from '../components/common/ProtectedRoute';
import { ResponsiveModal } from '../components/common';
import announcementApi, {
  Announcement, AnnouncementMeta, AttrMeta, AttrOption, AudienceDef, AudienceGroup, AudiencePreview, AudienceRule, AudienceSegment, Match,
} from '../services/announcementApi';
import { useAccess } from '../hooks/useAccess';
import { fa, num, jd, Explain, Card, Tag, useBeginner } from './FinanceAccountingKit';
import './EntityManagement.css';
import './FinanceAccounting.css';

const toEn = (s: string) => String(s || '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
const emptyDef = (): AudienceDef => ({ match: 'all', groups: [{ match: 'all', rules: [] }] });
const ruleCount = (d: AudienceDef) => d.groups.reduce((s, g) => s + g.rules.length, 0);
const sameDef = (a: AudienceDef, b: AudienceDef) => JSON.stringify(a) === JSON.stringify(b);
const splitIds = (txt: string) => txt.split(/[\n,،;\s]+/).map((s) => s.trim()).filter(Boolean);
/** نمونهٔ فرضی برای پیش‌نمایش جای‌گذاری‌ها */
const sampleFill = (s: string) => s.replace(/\{name\}|\{firstName\}/g, 'علی').replace(/\{points\}/g, '۱٬۲۰۰').replace(/\{wallet\}/g, '۳۵۰٬۰۰۰').replace(/\{phone\}/g, '۰۹۱۲…');

type TabKey = 'compose' | 'segments' | 'history';
const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'compose', label: 'ساخت مخاطب و ارسال', icon: <PeopleAltRoundedIcon /> },
  { key: 'segments', label: 'بخش‌های ذخیره‌شده', icon: <BookmarksRoundedIcon /> },
  { key: 'history', label: 'تاریخچهٔ ارسال', icon: <HistoryRoundedIcon /> },
];
const STATUS_FA: Record<string, { label: string; color: 'gray' | 'blue' | 'orange' | 'green' | 'red' }> = {
  draft: { label: 'پیش‌نویس', color: 'gray' }, scheduled: { label: 'زمان‌بندی‌شده', color: 'blue' }, sending: { label: 'در حال ارسال', color: 'orange' },
  sent: { label: 'ارسال شد', color: 'green' }, cancelled: { label: 'لغو شده', color: 'gray' }, failed: { label: 'ناموفق', color: 'red' },
};
const OP_FA: Record<string, string> = { gte: 'حداقل (≥)', lte: 'حداکثر (≤)', eq: 'دقیقاً (=)' };

const defaultRule = (a: AttrMeta): AudienceRule => {
  if (a.type === 'bool') return { key: a.key, value: true };
  if (a.type === 'days') return { key: a.key, op: (a.ops || ['gte'])[0], value: 7 };
  if (a.type === 'number') return { key: a.key, op: (a.ops || ['gte'])[0], value: 1 };
  if (a.type === 'enum') return { key: a.key, op: 'in', value: a.options?.[0] ? [a.options[0].value] : [] };
  return { key: a.key, op: 'in', value: [] };
};

/** انتخاب‌گر با جست‌وجو برای شرط‌های lookup (فروشگاه/محصول/نوع محصول/پیام قبلی) */
const LookupPicker: React.FC<{ kind: string; value: string[]; onChange: (v: string[]) => void }> = ({ kind, value, onChange }) => {
  const [opts, setOpts] = useState<AttrOption[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const cache = useRef(new Map<string, string>());
  const [, bump] = useState(0);
  useEffect(() => { // برچسب مقادیرِ از قبل انتخاب‌شده (مثلاً از یک بخش ذخیره‌شده)
    const missing = value.filter((v) => !cache.current.has(v));
    if (missing.length) announcementApi.lookup(kind, '', missing).then((r) => { (r.data || []).forEach((o) => cache.current.set(o.value, o.label)); bump((x) => x + 1); });
  }, [value, kind]);
  useEffect(() => {
    setLoading(true);
    const id = setTimeout(async () => { const r = await announcementApi.lookup(kind, q); (r.data || []).forEach((o) => cache.current.set(o.value, o.label)); setOpts(r.data || []); setLoading(false); }, 300);
    return () => clearTimeout(id);
  }, [q, kind]);
  return (
    <Autocomplete multiple size="small" options={opts} loading={loading} filterOptions={(x) => x}
      value={value.map((v) => ({ value: v, label: cache.current.get(v) || '…' }))}
      isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label}
      onChange={(_, v) => onChange(v.map((o) => o.value))} onInputChange={(_, v) => setQ(v)}
      renderInput={(p) => <TextField {...p} placeholder="جست‌وجو…" />} noOptionsText="چیزی پیدا نشد" />
  );
};

const AudienceMessaging: React.FC = () => {
  const navigate = useNavigate();
  const { has } = useAccess();
  const canCreate = has('admin_announcement_create');
  const canSend = has('admin_announcement_send');
  const beginner = useBeginner();

  const [tab, setTab] = useState<TabKey>('compose');
  const [meta, setMeta] = useState<AnnouncementMeta | null>(null);
  const attrs = useMemo(() => meta?.attributes || [], [meta]);
  const presets = useMemo(() => meta?.presets || [], [meta]);
  const placeholders = useMemo(() => meta?.placeholders || [], [meta]);
  const byKey = useMemo(() => new Map(attrs.map((a) => [a.key, a])), [attrs]);
  const groups = useMemo(() => Array.from(new Set(attrs.map((a) => a.group))), [attrs]);

  // ── کی‌ها؟
  const [def, setDef] = useState<AudienceDef>(emptyDef());
  const [includeTxt, setIncludeTxt] = useState('');
  const [excludeTxt, setExcludeTxt] = useState('');
  const [segment, setSegment] = useState<{ id: string; name: string } | null>(null);
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [counting, setCounting] = useState(false);

  // ── چی بگم؟
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [ch, setCh] = useState({ inApp: true, push: true, sms: false, email: false });
  const [smsText, setSmsText] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState<'title' | 'message' | 'sms'>('message');

  const [segments, setSegments] = useState<AudienceSegment[]>([]);
  const [segLoading, setSegLoading] = useState(false);
  const [history, setHistory] = useState<Announcement[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState<null | { id?: string; name: string; description: string }>(null);

  useEffect(() => { announcementApi.getMeta().then((r) => { if (r.success && r.data) setMeta(r.data); else toast.error(r.message || 'خطا'); }); }, []);
  const loadSegments = useCallback(async () => { setSegLoading(true); const r = await announcementApi.listSegments(); if (r.success) setSegments(r.data || []); else toast.error(r.message || 'خطا'); setSegLoading(false); }, []);
  const loadHistory = useCallback(async () => { setHistLoading(true); const r = await announcementApi.list({ page: 1, limit: 40 }); if (r.success) setHistory(r.data?.docs || []); else toast.error(r.message || 'خطا'); setHistLoading(false); }, []);
  useEffect(() => { if (tab === 'segments') loadSegments(); if (tab === 'history') loadHistory(); }, [tab, loadSegments, loadHistory]);

  const includeIds = useMemo(() => splitIds(includeTxt), [includeTxt]);
  const excludeIds = useMemo(() => splitIds(excludeTxt), [excludeTxt]);
  const audience = useMemo(() => ({ type: 'rules' as const, match: def.match, groups: def.groups, includeIds, excludeIds, segmentId: segment?.id, segmentName: segment?.name }), [def, includeIds, excludeIds, segment]);
  const hasAny = ruleCount(def) > 0 || includeIds.length > 0;

  // ── شمارش زنده
  const tick = useRef(0);
  useEffect(() => {
    if (!hasAny) { setPreview(null); return; }
    const my = ++tick.current; setCounting(true);
    const id = setTimeout(async () => {
      const r = await announcementApi.preview(audience);
      if (my !== tick.current) return;
      if (r.success && r.data) setPreview(r.data); else { setPreview(null); toast.error(r.message || 'خطا در شمارش'); }
      setCounting(false);
    }, 450);
    return () => clearTimeout(id);
  }, [audience, hasAny]);

  const activePreset = useMemo(() => presets.find((p) => sameDef(p.def, def))?.id, [presets, def]);
  const touch = (next: AudienceDef) => { setDef(next); setSegment(null); };
  const applyPreset = (id: string) => { const p = presets.find((x) => x.id === id); if (p) touch(JSON.parse(JSON.stringify(p.def))); };
  const pickSegment = (s: AudienceSegment) => { setDef(JSON.parse(JSON.stringify(s.def || { match: 'all', groups: [{ match: 'all', rules: s.rules }] }))); setSegment({ id: s._id, name: s.name }); setTab('compose'); };
  const setGroup = (gi: number, patch: Partial<AudienceGroup>) => touch({ ...def, groups: def.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) });
  const setRule = (gi: number, ri: number, patch: Partial<AudienceRule>) => setGroup(gi, { rules: def.groups[gi].rules.map((r, i) => (i === ri ? { ...r, ...patch } : r)) });
  const addRule = (gi: number, key: string) => { const a = byKey.get(key); if (a) setGroup(gi, { rules: [...def.groups[gi].rules, defaultRule(a)] }); };
  const removeRule = (gi: number, ri: number) => {
    const rules = def.groups[gi].rules.filter((_, i) => i !== ri);
    const gs = rules.length || def.groups.length === 1 ? def.groups.map((g, i) => (i === gi ? { ...g, rules } : g)) : def.groups.filter((_, i) => i !== gi);
    touch({ ...def, groups: gs });
  };
  const addGroup = () => touch({ ...def, groups: [...def.groups, { match: 'all', rules: [] }] });

  const reach = { inApp: preview?.total || 0, push: preview?.withPush || 0, sms: preview?.withPhone || 0, email: preview?.withEmail || 0 };
  const anyChannel = ch.inApp || ch.push || ch.sms || ch.email;
  const canGo = !!preview?.total && title.trim().length >= 3 && message.trim().length >= 3 && anyChannel;
  const payload = () => ({ title: title.trim(), message: message.trim(), linkUrl: linkUrl.trim() || undefined, channels: ch, smsText: ch.sms ? smsText.trim() || undefined : undefined, emailSubject: ch.email ? emailSubject.trim() || undefined : undefined, audience, notificationType: 'system' });

  const insertPh = (ph: string) => { if (focus === 'title') setTitle((t) => t + ph); else if (focus === 'sms') setSmsText((t) => t + ph); else setMessage((t) => t + ph); };
  const doTest = async () => {
    if (title.trim().length < 3 || message.trim().length < 3) { toast.info('اول عنوان و متن را بنویس'); return; }
    setBusy(true);
    const r = await announcementApi.sendTest({ title: title.trim(), message: message.trim(), linkUrl: linkUrl.trim() || undefined, notificationType: 'system' });
    setBusy(false);
    r.success ? toast.success('برای خودت فرستاده شد — گوشی/سایتت را چک کن') : toast.error(r.message || 'خطا');
  };
  const doSend = async (mode: 'now' | 'schedule' | 'draft') => {
    if (!canGo && mode !== 'draft') return;
    const at = mode === 'schedule' ? scheduledAt : undefined;
    if (mode === 'schedule' && !at) { toast.info('زمان ارسال را انتخاب کن'); return; }
    if (mode === 'now') {
      const lines = [`به ${num(preview?.total)} نفر ارسال می‌شود.`];
      if (ch.sms) lines.push(`• ${num(reach.sms)} پیامک — هزینه دارد`);
      if (ch.email) lines.push(`• ${num(reach.email)} ایمیل`);
      if (!window.confirm(lines.join('\n') + '\n\nمطمئنی؟')) return;
    }
    setBusy(true);
    const created = await announcementApi.create({ ...payload(), scheduledAt: at ? new Date(at).toISOString() : null } as any);
    if (!created.success || !created.data) { setBusy(false); toast.error(created.message || 'خطا در ذخیره'); return; }
    if (mode === 'now') { const s = await announcementApi.send(created.data._id); setBusy(false); if (!s.success) { toast.error(s.message || 'خطا در ارسال'); return; } toast.success('ارسال شروع شد — پیشرفتش را در «تاریخچهٔ ارسال» ببین'); }
    else { setBusy(false); toast.success(mode === 'schedule' ? 'زمان‌بندی شد' : 'پیش‌نویس ذخیره شد'); }
    setTitle(''); setMessage(''); setLinkUrl(''); setSmsText(''); setEmailSubject(''); setScheduledAt('');
    setTab('history');
  };
  const saveSegment = async () => {
    if (!saveOpen) return;
    if (saveOpen.name.trim().length < 2) { toast.info('نام بخش را بنویس'); return; }
    setBusy(true);
    const body = { name: saveOpen.name.trim(), description: saveOpen.description.trim(), def };
    const r = saveOpen.id ? await announcementApi.updateSegment(saveOpen.id, body) : await announcementApi.createSegment(body);
    setBusy(false);
    if (!r.success) { toast.error(r.message || 'خطا'); return; }
    toast.success(r.message || 'ذخیره شد');
    if (r.data) setSegment({ id: r.data._id, name: r.data.name });
    setSaveOpen(null); loadSegments();
  };

  const matchSwitch = (value: Match, onChange: (m: Match) => void, labels: [string, string] = ['همهٔ این‌ها', 'هر کدام از این‌ها']) => (
    <span className="am-match">
      <button className={value === 'all' ? 'on' : ''} onClick={() => onChange('all')}>{labels[0]}</button>
      <button className={value === 'any' ? 'on' : ''} onClick={() => onChange('any')}>{labels[1]}</button>
    </span>
  );

  const ruleRow = (gi: number, r: AudienceRule, ri: number) => {
    const a = byKey.get(r.key);
    if (!a) return null;
    let control: React.ReactNode;
    const opSel = (labels: [string, string]) => (
      <TextField select size="small" value={r.op === 'nin' ? 'nin' : 'in'} onChange={(e) => setRule(gi, ri, { op: e.target.value as any })} sx={{ minWidth: 86 }}><MenuItem value="in">{labels[0]}</MenuItem><MenuItem value="nin">{labels[1]}</MenuItem></TextField>
    );
    if (a.type === 'bool') {
      control = <TextField select size="small" fullWidth value={r.value ? '1' : '0'} onChange={(e) => setRule(gi, ri, { value: e.target.value === '1' })}><MenuItem value="1">{a.yes || 'بله'}</MenuItem><MenuItem value="0">{a.no || 'خیر'}</MenuItem></TextField>;
    } else if (a.type === 'number' || a.type === 'days') {
      const ops = a.ops || ['gte', 'lte', 'eq'];
      control = (
        <div style={{ display: 'flex', gap: 6 }}>
          <TextField select size="small" value={r.op || ops[0]} onChange={(e) => setRule(gi, ri, { op: e.target.value as any })} sx={{ minWidth: 118 }}>{ops.map((o) => <MenuItem key={o} value={o}>{OP_FA[o]}</MenuItem>)}</TextField>
          <TextField size="small" fullWidth value={String(r.value ?? '')} onChange={(e) => setRule(gi, ri, { value: Number(toEn(e.target.value).replace(/[^\d]/g, '')) || 0 })} InputProps={{ endAdornment: <span className="fac-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{a.unit}</span> }} />
        </div>
      );
    } else if (a.type === 'enum') {
      const v: string[] = Array.isArray(r.value) ? r.value : [];
      control = (
        <div style={{ display: 'flex', gap: 6 }}>
          {opSel(['یکی از', 'به‌جز'])}
          <TextField select size="small" fullWidth SelectProps={{ multiple: true, renderValue: (x: any) => (x as string[]).map((k) => a.options?.find((o) => o.value === k)?.label || k).join('، ') }} value={v} onChange={(e) => setRule(gi, ri, { value: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}>
            {(a.options || []).map((o) => <MenuItem key={o.value} value={o.value}><Checkbox size="small" checked={v.includes(o.value)} sx={{ p: 0, mr: 1 }} />{o.label}</MenuItem>)}
          </TextField>
        </div>
      );
    } else if (a.type === 'lookup') {
      control = (
        <div style={{ display: 'flex', gap: 6 }}>
          {opSel(['یکی از', 'به‌جز'])}
          <div style={{ flex: 1, minWidth: 0 }}><LookupPicker kind={a.lookup!} value={Array.isArray(r.value) ? r.value : []} onChange={(v) => setRule(gi, ri, { value: v })} /></div>
        </div>
      );
    } else {
      control = (
        <div style={{ display: 'flex', gap: 6 }}>
          {opSel(['شامل', 'به‌جز'])}
          <TextField size="small" fullWidth placeholder="تهران، البرز" value={Array.isArray(r.value) ? r.value.join('، ') : String(r.value ?? '')} onChange={(e) => setRule(gi, ri, { value: e.target.value.split(/[،,]/).map((s) => s.trim()).filter(Boolean) })} />
        </div>
      );
    }
    return (
      <React.Fragment key={`${r.key}-${ri}`}>
        {ri > 0 && <div className="am-and">{def.groups[gi].match === 'any' ? 'یا' : 'و'}</div>}
        <div className={`am-rule ${r.not ? 'neg' : ''}`}>
          <Tooltip title={r.not ? 'این شرط «نه» است — برعکسش' : 'برعکس کن: «نه»'} arrow><button className={`am-not ${r.not ? 'on' : ''}`} onClick={() => setRule(gi, ri, { not: !r.not })}>نه</button></Tooltip>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--color-text-primary)' }}><span className="fac-muted" style={{ fontSize: 10, fontWeight: 600, display: 'block' }}>{a.group}</span>{a.label}</div>
          <div className="am-rule-val">{control}</div>
          <Tooltip title="حذف شرط" arrow><button className="am-rule-x" onClick={() => removeRule(gi, ri)}><CloseRoundedIcon fontSize="small" /></button></Tooltip>
          {a.help && <div className="am-rule-help">{a.help}</div>}
        </div>
      </React.Fragment>
    );
  };

  const addSelect = (gi: number) => (
    <TextField select size="small" label="افزودن شرط" value="" onChange={(e) => addRule(gi, e.target.value)} sx={{ minWidth: 240 }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 420 } } } }}>
      {groups.map((g) => [
        <MenuItem key={`g-${g}`} disabled sx={{ fontSize: 11, fontWeight: 800, opacity: '1 !important', color: 'var(--color-primary)' }}>{g}</MenuItem>,
        ...attrs.filter((a) => a.group === g).map((a) => <MenuItem key={a.key} value={a.key} sx={{ pl: 3 }}>{a.label}</MenuItem>),
      ])}
    </TextField>
  );

  const chCard = (key: keyof typeof ch, label: string, desc: string, icon: React.ReactNode, n: number, cost = false) => (
    <div className={`am-ch ${ch[key] ? 'on' : ''} ${cost ? 'cost' : ''}`} onClick={() => setCh({ ...ch, [key]: !ch[key] })}>
      <Checkbox size="small" checked={ch[key]} sx={{ p: 0, mt: '2px' }} onChange={() => setCh({ ...ch, [key]: !ch[key] })} onClick={(e) => e.stopPropagation()} />
      <div className="am-ch-txt"><b style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{icon}{label}</b><span>{desc}</span></div>
      <div className="am-ch-n">{preview ? num(n) : '—'}<small>نفر می‌رسد</small></div>
    </div>
  );

  return (
    <ProtectedRoute requiredPermissions="admin_announcement_read">
      <Helmet><title>پیام به دستهٔ کاربران | پنل مدیریت</title></Helmet>
      <div className="entity-management fac-page">
        <div className="fac-current">
          <CampaignIcon />
          <div><b>پیام به دستهٔ کاربران</b><span>هر ترکیبی از شرط‌ها — و / یا / نه — با شمارش زنده، سپس ارسال از هر کانالی که بخواهی</span></div>
          <span style={{ flex: 1 }} />
          <button className={`fac-switch ${beginner.on ? 'on' : ''}`} onClick={beginner.toggle}><i />راهنما</button>
        </div>
        <div className="esc-tabs">{TABS.map((t) => <button key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>{t.icon}<em style={{ fontStyle: 'normal' }}>{t.label}</em></button>)}</div>

        {tab === 'compose' && (
          <>
            <Explain show={beginner.on} title="چطور شرط بچینم؟"
              text={<>شرط‌ها داخل <b>گروه</b> می‌نشینند. داخل هر گروه می‌گویی «همهٔ این‌ها» (و) یا «هر کدام» (یا)؛ بین گروه‌ها هم همین‌طور. هر شرط یک دکمهٔ <b>«نه»</b> دارد که برعکسش می‌کند. برای عدد می‌توانی ≥ ≤ = بگذاری و یک ویژگی را دو بار بیاوری تا بازه بسازی («ثبت‌نام ≥ ۳۰ روز» و «ثبت‌نام ≤ ۹۰ روز»). پایین هم می‌توانی شماره‌هایی را <b>به‌علاوه</b> یا <b>به‌جز</b> کنی. در متن پیام از <b>{'{name}'}</b> و مانندش استفاده کن تا برای هر نفر شخصی شود.</>}
              how={['یک دستهٔ آماده بزن و بعد دست‌کاری‌اش کن.', 'جملهٔ خاکستری زیر شمارش همان شرط‌هاست به زبان آدمیزاد — بخوانش تا مطمئن شوی درست چیده‌ای.', 'اول «تست روی خودم»، بعد «ارسال».']} />

            <div className="am-grid">
              <Card title="کی‌ها؟" sub="گروه‌های شرط با و / یا / نه" icon={<PeopleAltRoundedIcon />}
                actions={ruleCount(def) > 0 && canCreate ? <Button size="small" variant="outlined" startIcon={<SaveRoundedIcon />} onClick={() => setSaveOpen({ id: segment?.id, name: segment?.name || '', description: '' })}>{segment ? 'به‌روزرسانی بخش' : 'ذخیره به‌عنوان بخش'}</Button> : undefined}>
                <div className="am-presets">
                  {presets.map((p) => <Tooltip key={p.id} title={p.help} arrow><button className={activePreset === p.id ? 'on' : ''} onClick={() => applyPreset(p.id)}>{p.label}</button></Tooltip>)}
                  {ruleCount(def) > 0 && <button onClick={() => { touch(emptyDef()); setIncludeTxt(''); setExcludeTxt(''); }} style={{ color: 'var(--color-error-dark)' }}>پاک کردن</button>}
                </div>
                {segment && <p className="fac-note" style={{ marginTop: 8 }}>از بخش ذخیره‌شدهٔ «{segment.name}» — اگر شرطی را عوض کنی، ارتباطش قطع می‌شود تا بخش اصلی دست نخورد.</p>}

                <div style={{ marginTop: 12 }}>
                  {def.groups.map((g, gi) => (
                    <React.Fragment key={gi}>
                      {gi > 0 && <div className="am-between">{matchSwitch(def.match, (m) => touch({ ...def, match: m }), ['و', 'یا'])}</div>}
                      <div className="am-group">
                        <div className="am-group-head">
                          <b>گروه {fa(gi + 1)}</b>
                          {g.rules.length > 1 && matchSwitch(g.match, (m) => setGroup(gi, { match: m }))}
                          <span style={{ flex: 1 }} />
                          {def.groups.length > 1 && <Button size="small" color="error" onClick={() => touch({ ...def, groups: def.groups.filter((_, i) => i !== gi) })}>حذف گروه</Button>}
                        </div>
                        <div className="am-rules">{g.rules.length === 0 ? <div className="fac-empty" style={{ padding: 14 }}>شرطی ندارد — از پایین اضافه کن.</div> : g.rules.map((r, ri) => ruleRow(gi, r, ri))}</div>
                        <div className="am-add">{addSelect(gi)}</div>
                      </div>
                    </React.Fragment>
                  ))}
                  <Button size="small" variant="outlined" onClick={addGroup}>+ گروه جدید (برای «یا»)</Button>
                </div>

                <div className="am-extra">
                  <TextField size="small" multiline minRows={2} label="به‌علاوهٔ این‌ها (شماره/ایمیل، هر خط یکی)" value={includeTxt} onChange={(e) => setIncludeTxt(e.target.value)} placeholder="0912…" helperText="فارغ از شرط‌ها حتماً دریافت می‌کنند" />
                  <TextField size="small" multiline minRows={2} label="به‌جز این‌ها" value={excludeTxt} onChange={(e) => setExcludeTxt(e.target.value)} placeholder="0912…" helperText="حتی اگر شرط‌ها برقرار باشد، نمی‌گیرند" />
                </div>

                <div className={`am-count ${preview?.total ? 'has' : ''}`}>
                  <div className="am-count-top">
                    {counting ? <CircularProgress size={22} /> : <b>{hasAny ? num(preview?.total ?? 0) : '—'}</b>}
                    <span>{hasAny ? 'نفر با این شرط‌ها' : 'اول شرط بگذار'}</span>
                  </div>
                  {preview && (
                    <>
                      <div className="am-reach">
                        <div className={reach.push ? '' : 'zero'}>پوش <b>{num(reach.push)}</b></div>
                        <div className={reach.sms ? '' : 'zero'}>پیامک <b>{num(reach.sms)}</b> <small className="fac-muted">(موبایل تأییدشده)</small></div>
                        <div className={reach.email ? '' : 'zero'}>ایمیل <b>{num(reach.email)}</b></div>
                        <div>درون‌برنامه <b>{num(reach.inApp)}</b></div>
                      </div>
                      {preview.describe && <div className="am-describe">{preview.describe}{includeIds.length ? ` + ${fa(includeIds.length)} نفر دستی` : ''}{excludeIds.length ? ` − ${fa(excludeIds.length)} نفر مستثنا` : ''}</div>}
                      {!!preview.notFound?.length && <p className="fac-note" style={{ color: 'var(--color-warning-dark)' }}>پیدا نشد: {preview.notFound.join('، ')}</p>}
                      {preview.sample.length > 0 && (
                        <div className="am-sample">
                          {preview.sample.map((u) => <span key={u._id}>{u.name}<i>{u.phoneNumber || u.email || ''}</i></span>)}
                          {preview.total > preview.sample.length && <span>و {num(preview.total - preview.sample.length)} نفر دیگر…</span>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>

              <Card title="چی بگم؟" sub="پیام شخصی‌سازی‌شده و کانال‌ها" icon={<SendRoundedIcon />}>
                <div className="fac-form">
                  <TextField className="wide" size="small" label="عنوان" value={title} onFocus={() => setFocus('title')} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 100 }} helperText={`${fa(title.length)}/۱۰۰`} />
                  <TextField className="wide" size="small" label="متن پیام" value={message} onFocus={() => setFocus('message')} onChange={(e) => setMessage(e.target.value)} multiline minRows={3} inputProps={{ maxLength: 500 }} helperText={`${fa(message.length)}/۵۰۰`} />
                  {placeholders.length > 0 && (
                    <div className="wide">
                      <div className="fac-muted" style={{ fontSize: 11 }}>جای‌گذاری (در فیلدی که آخرین بار کلیک کرده‌ای می‌نشیند):</div>
                      <div className="am-ph">{placeholders.map((p) => <Tooltip key={p.key} title={p.help || p.label} arrow><button onClick={() => insertPh(p.key)}>{p.key} {p.label}</button></Tooltip>)}</div>
                    </div>
                  )}
                  <TextField className="wide" size="small" label="مقصد (اختیاری) — مثلاً /products یا /user/orders" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} error={!!linkUrl && !linkUrl.startsWith('/')} helperText={linkUrl && !linkUrl.startsWith('/') ? 'باید با / شروع شود' : 'با لمس پیام، کاربر به این صفحه می‌رود'} />
                </div>

                <div style={{ fontSize: 12.5, fontWeight: 800, margin: '14px 0 8px' }}>از کدام راه؟</div>
                <div className="am-channels">
                  {chCard('inApp', 'درون‌برنامه', 'در زنگولهٔ سایت و اپ؛ رایگان', <NotificationsActiveRoundedIcon fontSize="small" />, reach.inApp)}
                  {chCard('push', 'پوش', 'اعلان روی گوشی/مرورگر؛ رایگان', <PhoneIphoneRoundedIcon fontSize="small" />, reach.push)}
                  {chCard('sms', 'پیامک', 'فقط موبایل تأییدشده؛ هر پیامک هزینه دارد', <SmsRoundedIcon fontSize="small" />, reach.sms, true)}
                  {chCard('email', 'ایمیل', 'فقط کسانی که ایمیل دارند', <EmailRoundedIcon fontSize="small" />, reach.email, true)}
                </div>
                {ch.sms && <TextField fullWidth size="small" sx={{ mt: 1.5 }} label="متن پیامک (خالی = همان متن پیام)" value={smsText} onFocus={() => setFocus('sms')} onChange={(e) => setSmsText(e.target.value)} multiline minRows={2} inputProps={{ maxLength: 300 }} helperText={`${fa((smsText || message).length)}/۳۰۰ · ≈ ${fa(Math.max(1, Math.ceil((smsText || message).length / 70)))} صفحهٔ پیامک`} />}
                {ch.email && <TextField fullWidth size="small" sx={{ mt: 1.5 }} label="موضوع ایمیل (خالی = عنوان)" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} inputProps={{ maxLength: 120 }} />}

                {(title || message) && (
                  <div className="am-preview" style={{ marginTop: 14 }}>
                    <div className="am-preview-title">{sampleFill(title) || 'عنوان'}</div>
                    <div className="am-preview-body">{sampleFill(message) || 'متن پیام'}</div>
                    <div className="am-preview-meta">نمونه برای یک کاربر فرضی{linkUrl ? ` · لمس ⟶ ${linkUrl}` : ''}</div>
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  <TextField fullWidth size="small" type="datetime-local" label="زمان ارسال (اختیاری — خالی = الان)" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} InputLabelProps={{ shrink: true }} helperText={scheduledAt ? `ارسال در ${jd(new Date(scheduledAt).toISOString(), true)}` : 'خالی بگذاری یعنی همین حالا'} />
                </div>

                <div className="am-send">
                  <div className="am-send-sum">
                    {preview?.total ? `${num(preview.total)} گیرنده` : 'گیرنده‌ای انتخاب نشده'}
                    <span>{[ch.inApp && 'درون‌برنامه', ch.push && 'پوش', ch.sms && 'پیامک', ch.email && 'ایمیل'].filter(Boolean).join(' · ') || 'هیچ کانالی فعال نیست'}</span>
                  </div>
                  <span style={{ flex: 1 }} />
                  <Button size="small" variant="outlined" disabled={busy} startIcon={<ScienceRoundedIcon />} onClick={doTest}>تست روی خودم</Button>
                  {canCreate && <Button size="small" disabled={busy || !hasAny} onClick={() => doSend('draft')}>پیش‌نویس</Button>}
                  {canSend && scheduledAt && <Button size="small" variant="outlined" disabled={busy || !canGo} onClick={() => doSend('schedule')}>زمان‌بندی</Button>}
                  {canSend && !scheduledAt && <Button size="small" variant="contained" color="success" disabled={busy || !canGo} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <SendRoundedIcon />} onClick={() => doSend('now')}>ارسال به {preview?.total ? num(preview.total) : '…'} نفر</Button>}
                </div>
              </Card>
            </div>
          </>
        )}

        {tab === 'segments' && (
          <Card title="بخش‌های ذخیره‌شده" sub="دسته‌هایی که یک بار ساخته‌ای — همیشه زنده‌اند و لحظهٔ ارسال دوباره شمرده می‌شوند" icon={<BookmarksRoundedIcon />}
            actions={<Button size="small" startIcon={segLoading ? <CircularProgress size={13} /> : <RefreshIcon />} disabled={segLoading} onClick={loadSegments}>به‌روزرسانی</Button>}>
            {segLoading && !segments.length ? <div className="fac-loading"><CircularProgress /></div>
              : segments.length === 0 ? <div className="fac-empty">هنوز بخشی ذخیره نکرده‌ای — در تب اول شرط بچین و «ذخیره به‌عنوان بخش» را بزن.</div>
                : segments.map((s) => (
                  <div key={s._id} className="am-seg">
                    <div className="am-seg-n"><b>{s.lastCount != null ? num(s.lastCount) : '—'}</b><small>نفر · {s.lastCountAt ? jd(s.lastCountAt) : '—'}</small></div>
                    <div className="am-seg-txt">
                      <b>{s.name}</b>{s.description && <span>{s.description}</span>}
                      <span style={{ marginTop: 4 }}>{s.describeFull || (s.describe || []).join(' و ')}</span>
                      <span className="fac-muted" style={{ fontSize: 10.5, marginTop: 4 }}>{fa(s.usageCount || 0)} بار استفاده شده</span>
                    </div>
                    <div className="am-seg-acts">
                      <Button size="small" variant="contained" onClick={() => pickSegment(s)}>استفاده</Button>
                      <Tooltip title="شمارش مجدد" arrow><Button size="small" onClick={async () => { const r = await announcementApi.countSegment(s._id); if (r.success) { toast.success(`${num(r.data?.total)} نفر`); loadSegments(); } else toast.error(r.message || 'خطا'); }}><RefreshIcon fontSize="small" /></Button></Tooltip>
                      {canCreate && <Tooltip title="ویرایش" arrow><Button size="small" onClick={() => { pickSegment(s); setSaveOpen({ id: s._id, name: s.name, description: s.description || '' }); }}><EditRoundedIcon fontSize="small" /></Button></Tooltip>}
                      {has('admin_announcement_delete') && <Tooltip title="حذف" arrow><Button size="small" color="error" onClick={async () => { if (!window.confirm(`بخش «${s.name}» حذف شود؟`)) return; const r = await announcementApi.deleteSegment(s._id); r.success ? loadSegments() : toast.error(r.message || 'خطا'); }}><DeleteOutlineRoundedIcon fontSize="small" /></Button></Tooltip>}
                    </div>
                  </div>
                ))}
          </Card>
        )}

        {tab === 'history' && (
          <Card title="تاریخچهٔ ارسال" sub="همهٔ پیام‌های گروهی — با آمار هر کانال" icon={<HistoryRoundedIcon />}
            actions={<><Button size="small" variant="outlined" onClick={() => navigate('/admin/announcements')}>مدیریت کامل اطلاعیه‌ها</Button><Button size="small" startIcon={histLoading ? <CircularProgress size={13} /> : <RefreshIcon />} disabled={histLoading} onClick={loadHistory}>به‌روزرسانی</Button></>}>
            {histLoading && !history.length ? <div className="fac-loading"><CircularProgress /></div>
              : history.length === 0 ? <div className="fac-empty">هنوز پیامی ارسال نشده</div>
                : history.map((h) => {
                  const st = STATUS_FA[h.status] || STATUS_FA.draft; const s = h.stats || ({} as any);
                  const n = h.audience?.groups?.reduce((x, g) => x + g.rules.length, 0) ?? h.audience?.rules?.length ?? 0;
                  const who = h.audience?.type === 'rules' ? (h.audience.segmentName ? `بخش «${h.audience.segmentName}»` : `${fa(n)} شرط`) : h.audience?.type === 'all' ? 'همه' : h.audience?.type === 'sellers' ? 'فروشندگان' : h.audience?.type === 'buyers' ? 'خریداران' : h.audience?.type === 'specific' ? 'کاربران مشخص' : 'بخش‌بندی قدیمی';
                  return (
                    <div key={h._id} className="am-hist">
                      <div><b>{h.title}</b><small>{h.message.slice(0, 70)}{h.message.length > 70 ? '…' : ''}</small></div>
                      <div><Tag color={st.color}>{st.label}</Tag></div>
                      <div>
                        <span className="am-stat">گیرنده <b>{num(s.targeted)}</b></span>
                        {h.channels?.push && <span className="am-stat">پوش <b>{num(s.pushSent)}</b></span>}
                        {h.channels?.sms && <span className="am-stat">پیامک <b>{num(s.smsSent)}</b>{s.smsFailed ? <i className="fac-neg" style={{ fontStyle: 'normal' }}>/{num(s.smsFailed)}✗</i> : null}</span>}
                        {h.channels?.email && <span className="am-stat">ایمیل <b>{num(s.emailSent)}</b></span>}
                      </div>
                      <div><small>{who}</small><small>{jd(h.completedAt || h.scheduledAt || h.createdAt, true)}</small></div>
                      <div style={{ textAlign: 'left' }}>
                        {h.status === 'scheduled' && has('admin_announcement_update') && <Button size="small" color="warning" onClick={async () => { const r = await announcementApi.cancel(h._id); r.success ? loadHistory() : toast.error(r.message || 'خطا'); }}>لغو</Button>}
                        {h.status === 'draft' && canSend && <Button size="small" variant="contained" onClick={async () => { if (!window.confirm('ارسال شود؟')) return; const r = await announcementApi.send(h._id); r.success ? loadHistory() : toast.error(r.message || 'خطا'); }}>ارسال</Button>}
                      </div>
                    </div>
                  );
                })}
          </Card>
        )}
      </div>

      <ResponsiveModal open={!!saveOpen} onClose={() => !busy && setSaveOpen(null)} title={saveOpen?.id ? 'به‌روزرسانی بخش' : 'ذخیره به‌عنوان بخش'} subtitle="تا دفعهٔ بعد با یک کلیک همین دسته را داشته باشی" icon={<SaveRoundedIcon />}
        actions={<><Button size="small" onClick={() => setSaveOpen(null)} disabled={busy}>انصراف</Button><Button size="small" variant="contained" disabled={busy} onClick={saveSegment}>ذخیره</Button></>}>
        {saveOpen && (
          <div className="fac-form" dir="rtl">
            <TextField className="wide" size="small" label="نام بخش" value={saveOpen.name} onChange={(e) => setSaveOpen({ ...saveOpen, name: e.target.value })} placeholder="مثلاً: خریداران بدون اپ" autoFocus />
            <TextField className="wide" size="small" label="توضیح (اختیاری)" value={saveOpen.description} onChange={(e) => setSaveOpen({ ...saveOpen, description: e.target.value })} />
            <div className="wide fac-muted" style={{ fontSize: 11.5, lineHeight: 1.8 }}>{fa(ruleCount(def))} شرط · الان {preview ? `${num(preview.total)} نفر` : '…'}{preview?.describe ? ` · ${preview.describe}` : ''}</div>
          </div>
        )}
      </ResponsiveModal>
    </ProtectedRoute>
  );
};

export default AudienceMessaging;
